import type { RangeItem } from './wallet-dashboard.wire';

// Links do painel para a lista de Cobranças (`/gestao-carteira/cobrancas`), com os mesmos parâmetros do legado na
// busca da lista (U22). A lista lê as datas como `yyyy-MM-dd` (`billingFilters`, tipo `dateRange`). O bloco não
// navega (U41): pede a ação `walletDashboard.openBillings` com esta busca, e o hospedeiro leva até lá.

export const BILLING_LIST_PATH = '/gestao-carteira/cobrancas';

export interface BillingLinkSearch {
  readonly initialDueDate?: string;
  readonly finalDueDate?: string;
  readonly initialCreatedDate?: string;
  readonly finalCreatedDate?: string;
  readonly statusesValue?: string[];
  readonly walletsCode?: string[];
}

/** A data civil de um `initDate`/`endDate` da API (o legado põe o texto cru na URL e a lista faz `new Date`). */
const civilOf = (value: string | null | undefined) => (value && /^\d{4}-\d{2}-\d{2}/.test(value) ? value.slice(0, 10) : undefined);

const wallets = (walletsCode: readonly string[] | undefined) => (walletsCode && walletsCode.length > 0 ? { walletsCode: [...walletsCode] } : {});

const defined = (search: Record<string, string | string[] | undefined>): BillingLinkSearch =>
  Object.fromEntries(Object.entries(search).filter(([, value]) => value !== undefined));

export type SummaryLink = 'registered' | 'paid' | 'open';

/**
 * Registrados / Pagos / Total em aberto (BillingSummaryGrid.tsx:34-38,51,61,72): vencimento no período e as
 * carteiras. "Pagos" leva `statusesValue=Paid`. "Total em aberto" leva `ExcludeStatusesValue=Paid` no legado, que a
 * lista de Cobranças não lê (BillingListContainer.tsx:325-380): aqui fica igual a "Registrados" (⚠️ no relatório).
 */
export function summaryLinkSearch(kind: SummaryLink, filters: { readonly initialDate: string; readonly finalDate: string; readonly walletsCode?: readonly string[] | undefined }): BillingLinkSearch {
  return defined({
    initialDueDate: filters.initialDate,
    finalDueDate: filters.finalDate,
    ...wallets(filters.walletsCode),
    // eslint-disable-next-line lingui/no-unlocalized-strings -- valor do enum de status do fio
    ...(kind === 'paid' ? { statusesValue: ['Paid'] } : {}),
  });
}

/** Faixa sem as duas datas (o tipo plano do legado, ou data fora do formato): não vira link. */
const dated = (item: RangeItem | null | undefined) => civilOf(item?.initDate) !== undefined && civilOf(item?.endDate) !== undefined;

/** Faixa das cobranças em aberto (BillingWalletRegistered.tsx:104-107): criação de `initDate` a `endDate`. */
export function openRangeSearch(item: RangeItem | null | undefined, walletsCode: readonly string[] | undefined): BillingLinkSearch | null {
  if (!dated(item)) return null;
  return defined({ finalCreatedDate: civilOf(item?.endDate), initialCreatedDate: civilOf(item?.initDate), ...wallets(walletsCode) });
}

/**
 * Faixa das cobranças vencidas (TotalOverdueCharges.tsx:108-111): o legado troca as pontas, `finalDueDate=initDate` e
 * `initialDueDate=endDate` (a faixa vencida conta para trás). Mantido.
 */
export function overdueRangeSearch(item: RangeItem | null | undefined, walletsCode: readonly string[] | undefined): BillingLinkSearch | null {
  if (!dated(item)) return null;
  return defined({ finalDueDate: civilOf(item?.initDate), initialDueDate: civilOf(item?.endDate), ...wallets(walletsCode) });
}
